import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import type { GetServerSideProps } from 'next/types';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import useSWR from 'swr';
import { RC_DURATION, REEBELO_CURRENCY, TagHelpers } from '@lambda/reebelo';
import {
  calculatePriceAfterSubsidy,
  extractTags,
} from '@lambda/reebelo/src/tagHelpers';
import { CartLine, Cart } from '@lambda/commons/apis/shopify/types/storefront';
import axios from 'axios';
import Image from 'next/image';
import { isEmpty } from 'lodash-es';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import Loading from '@/components/Loading/Loading';
import i18n from '@/settings/i18n';
import GTM from '@/lib/google-tag-manager';
import shopify from '@/lib/shopify';
import useCart, { encodeDecodeForAttr, initializeCart } from '@/lib/use-cart';
import useWindowSize from '@/lib/use-window-size';
import settings, { priceFormater } from '@/settings';
import {
  CartInfo,
  CartItem,
  EmptyCart,
  BreadCrumb,
  CartStaticInfo,
  RecommendedItems,
  CartItemRemovedModal,
} from '@/components/cart';
import { UpgradedGuidePopup } from '@/components/cart/CartItem';
import { AppCtx } from './_app';
import { ItemPskuMap, RecommendedItemT } from '@/components/cart/types';
import {
  SHOPIFY_CART_QUERY,
  findOutOfStockCartLineItems,
  deleteCartLines,
} from '@/lib/cart';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import { trackCartViewed } from '@/lib/analytics/trackCart';
import { logger } from '@/lib/logger';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { trackCheckoutStarted } from '@/lib/analytics/trackCheckout';
import WarrantyUpsell from '@/components/cart/warranty-upsell/WarrantyUpsell';
import { selectWarranty } from '@/lib/collections/catalogue/helpers';
import { analytics } from '@/lib/analytics/vendor/segment/segment';
import {
  generateCartItemsCount,
  PricePayload,
  putRCareItemsNextToProduct,
  reconcileEsAndShopfiyPrice,
  updateProdQtyWithRCareAndEShipping,
} from '@/components/cart/cartUtils';
import { warrantyUpsellT } from '@/lib/reebelocare/types';
import PopularCategories from '@/components/commons/PopularCategories';
import BestSellers from '@/components/cart/BestSellers';
import TradeInItem from '@/components/cart/TradeInItem';
import { useBuybackContext } from '@/lib/buyback/BuybackContext';

const BackToTopButton = dynamic(
  () => import('@/components/collections/BackToTopButton'),
  { ssr: false },
);
const PaymentIcons = dynamic(
  () => import('@/components/collections/[handle]/NewProductPage/PaymentIcons'),
  { ssr: false },
);

const t = i18n.init();

type Props = {
  shouldRefreshCart: boolean;
};

type Path = {
  /** product id to add to cart */ add?: string;
  /** 'true' or undefined */ buyNow?: string;
  /** 'true' or undefined */ warranty?: string;
};

export const CartContext = createContext<ReturnType<typeof useCart>>({} as any);

const CART_COOKIE = 'cart-id';

export const getServerSideProps: GetServerSideProps<Props, Path> = async ({
  req,
  res,
}) => {
  const cartId = req.cookies[CART_COOKIE] as string | undefined;

  if (!cartId) return { props: { shouldRefreshCart: false } };

  global.CART_ID_SERVER = cartId;

  const raw = req.cookies['reebelo-cart-product'];
  let pricePayload: PricePayload | undefined;

  if (raw) {
    try {
      pricePayload = JSON.parse(decodeURIComponent(raw));
    } catch (e) {
      logger.error({ e, raw }, 'Invalid price‐check cookie');
    }

    res.setHeader('Set-Cookie', 'reebelo-cart-product=; Max-Age=0; Path=/');
  }

  const response = await shopify.fetchSafe({
    query: `query ($id: ID!) { cart(id: $id) {${SHOPIFY_CART_QUERY}}}`,
    variables: { id: cartId },
  });

  if (response == null) return { props: { shouldRefreshCart: false } };

  reconcileEsAndShopfiyPrice(response.cart as Cart, pricePayload);

  const cartUpdates = updateProdQtyWithRCareAndEShipping(response.cart as Cart);

  if (cartUpdates.length > 0) {
    try {
      await shopify.fetchSafe({
        query: `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
                cartLinesUpdate(cartId: $cartId, lines: $lines) {
                  cart { ${SHOPIFY_CART_QUERY} }
                  userErrors { field message }
                }
              }`,
        variables: { cartId, lines: cartUpdates },
      });
    } catch (error) {
      logger.error({ error }, 'error updating cart');
    }
  }

  return {
    props: {
      shouldRefreshCart: cartUpdates.length > 0,
    },
  };
};

const totalCompareAt = (cart: Cart, comparePrices: Record<string, number>) =>
  cart.lines.edges
    .map((edge) => {
      const isReebeloCare =
        edge.node.merchandise.product.title.includes('ReebeloCare');
      const isExpressShipping = edge.node.merchandise.product.title
        .toLowerCase()
        .includes('express shipping');
      const isScreenProtector = edge.node.attributes.some(
        (attribute) => attribute.key === 'protector',
      );

      const comparePrice = edge.node.attributes.find(
        (a) => a.key === '_compare_price',
      )?.value;

      const { subsidy, msrp } = extractTags(edge.node.merchandise.product.tags);
      const subsidizedItemWithoutComparePrice =
        subsidy.length !== 0 && !comparePrice && msrp == null;

      if (
        isReebeloCare ||
        isScreenProtector ||
        isExpressShipping ||
        subsidizedItemWithoutComparePrice
      ) {
        const nominalPrice = Number(edge.node.merchandise.priceV2.amount || 0);
        const price =
          subsidy.length !== 0
            ? calculatePriceAfterSubsidy(nominalPrice, subsidy)
            : nominalPrice;

        return {
          comparePrice: price,
          productComparePrice: price,
          priceV2: price,
          quantity: edge.node.quantity,
        };
      }

      return {
        comparePrice: comparePrices[edge.node.id],
        productComparePrice: Number(comparePrice) || 0,
        priceV2: Number(edge.node.merchandise.priceV2.amount) || 0,
        quantity: edge.node.quantity,
      };
    })
    .map(
      ({ comparePrice, productComparePrice, priceV2, quantity }) =>
        Math.max(comparePrice, productComparePrice, priceV2) * quantity,
    )
    .reduce((acc, val) => acc + val, 0);

const makeLineItemsQS = (lineItems: CartLine[]): string =>
  lineItems
    .reduce((prevQS, item) => {
      const { psku } = extractTags(item.merchandise.product.tags);

      // add all products have warrantyEligibilityMap is true
      if (!psku) return prevQS;

      return `${prevQS}&pskus=${psku}`;
    }, '')
    .slice(1);

const LoadingCart = () => (
  <>
    <Head>
      <title>{t`Your Shopping Cart`}</title>
      <WebsiteSchema />
    </Head>
    <div className="reebelo-container pt-5 pb-10">
      <div className="mx-auto w-full max-w-[500px] py-12 text-center md:py-32">
        <Loading className="mb-16 mt-4" />
      </div>
    </div>
  </>
);

const fetchUpdatedWarrantyUpsellList = async (
  lineItems: CartLine[],
  warrantyEligibilityMap: Record<string, boolean>,
) => {
  const result = await Promise.all(
    lineItems.map(async (line) => {
      const {
        product: { productType, tags },
        priceV2: { amount },
      } = line.merchandise;

      const variant = TagHelpers.extractTags(tags);
      const price = Number(amount);

      const finalPrice = TagHelpers.calculatePriceAfterSubsidy(
        price,
        variant.subsidy,
      );

      const productWarranty: Partial<Record<RC_DURATION, WarrantyT>> = {};

      const rcDurations = Object.values(RC_DURATION);

      // eslint-disable-next-line no-restricted-syntax
      for (const _duration of rcDurations) {
        const duration = _duration as RC_DURATION;

        // eslint-disable-next-line no-await-in-loop
        const productWarrantyDynamicDuration = await selectWarranty(
          finalPrice,
          duration,
          productType,
        );

        if (productWarrantyDynamicDuration)
          productWarranty[duration] = productWarrantyDynamicDuration;
      }

      if (
        productWarranty[RC_DURATION['24_MONTH']] != null &&
        variant.psku &&
        warrantyEligibilityMap?.[variant.psku]
      ) {
        const addedWarranty = lineItems.find(
          (l) =>
            encodeDecodeForAttr.decode(l.attributes) === line.merchandise.sku &&
            l.merchandise.product.title.toLowerCase().includes('reebelocare'),
        );

        if (addedWarranty) return null;

        return {
          psku: variant.psku,
          productWarranty,
          device: line,
        };
      }

      return null;
    }),
  );

  return result.filter(Boolean);
};

export default function CartPage(props: Props) {
  const { cart, isLoadingCart, setIsLoadingCart, setCart, setCartCount } =
    useContext(AppCtx);
  const { includesTradeIn } = useBuybackContext();
  const [isModalVisible, setModalVisible] = useState(false);
  const [lineItemsToBeRemoved, setLineItemsToBeRemoved] = useState<string[]>(
    [],
  );
  const CartHelpers = useCart();
  const [windowWidth] = useWindowSize();
  const isSmallerWindow = windowWidth < 1280;
  const { zipcodeStateData } = useContext(AppCtx);
  const { loading, scrollPos } = CartHelpers;
  const [isItemsVisible, toggleItems] = useState(!isSmallerWindow);
  const [comparePrices, setComparePrices] = useState<Record<string, number>>(
    {},
  );
  const { customer } = useCustomerContext();

  const [showWarrantyUpsell, setShowWarrantyUpsell] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const router = useRouter();

  const [warrantyEligibilityMap, setWarrantyEligibilityMap] = useState<
    Record<string, boolean>
  >({});

  const [lang, setLang] = useState('');

  useEffect(() => {
    if (cart?.lines?.edges) {
      const query = cart.lines.edges
        .map((e) => {
          const { product } = e.node.merchandise;
          const { psku } = extractTags(product.tags || []);

          return psku;
        })
        .join('|');

      axios
        .get(`api/cart/warranty-eligiblity?pskus=${query}`)
        .then((res) => setWarrantyEligibilityMap(res.data));
    }
  }, [cart]);

  const lineItems = useMemo(
    () => cart?.lines.edges.map((e) => e.node) || [],
    [cart],
  );

  const categories = useMemo(
    () =>
      lineItems.map(
        (item) =>
          item.merchandise.product.productType ??
          extractTags(item.merchandise.product.tags).category,
      ),
    [lineItems],
  );

  useEffect(() => {
    if (props.shouldRefreshCart) {
      initializeCart(setIsLoadingCart).then(
        ({ cart: cartResult, count: countResult }) => {
          setCart(cartResult);
          setCartCount(countResult);
        },
      );
    }
  }, [props.shouldRefreshCart]);

  useEffect(() => {
    toggleItems(!isSmallerWindow);
  }, [isSmallerWindow]);

  useEffect(() => {
    if (cart == null) return;

    trackCartViewed(cart);

    GTM.event('view_cart', {
      currency: REEBELO_CURRENCY[settings.store],
      value: Number(cart.estimatedCost.subtotalAmount.amount),
      items: cart.lines.edges
        .map((l) => l.node)
        .map((l) => ({
          item_id: l.id,
          item_name: l.merchandise.product.title,
        })),
    });
    // If this dependency array is empty it will hit cart == null due to race condition on mount since cart is pulled from ctx.
    // Although adding it to the array will cause useEffect to rerun on cart modifications
  }, [cart]);

  const isInvalidCart = cart == null || lineItems.length === 0;
  const recommendedItemsQS = makeLineItemsQS(lineItems);

  const fetcher = (url: string) => fetch(url).then((res) => res.json());
  const {
    data: recommendations,
    error: recommendationsError,
    isValidating,
  } = useSWR<RecommendedItemT[]>(
    isInvalidCart ? null : `/api/cart/recommended-offers?${recommendedItemsQS}`,
    fetcher,
  );

  const recommendationsLoading = isValidating && !recommendations;

  if (recommendationsError)
    logger.error({ recommendationsError }, 'Recommendations error');

  useEffect(() => {
    window.scrollTo({ top: scrollPos as number, left: 0 });
  }, [recommendations]);

  const [isUpgradeGuideOpen, setIsUpgradedGuideOpen] = useState({
    open: false,
    index: 0,
  });

  const handleUpgradedOpen = (data: UpgradedGuidePopup) => {
    setIsUpgradedGuideOpen({ open: data.open, index: data.index });
  };
  const [rearrangedLineItems, setRearrangedLineItems] = useState<CartLine[]>(
    [],
  );
  const [cartItemsWithoutRCare, setCartItemsWithoutRCare] = useState<
    CartLine[]
  >([]);

  useEffect(() => {
    if (!lineItems.length) {
      setRearrangedLineItems([]);
      setCartItemsWithoutRCare([]);

      return;
    }

    const processItems = async () => {
      try {
        const res = await putRCareItemsNextToProduct(lineItems);

        setRearrangedLineItems(res.rearrangedLineItems);
        setCartItemsWithoutRCare(res.cartItemsWithoutRCare);
      } catch (error) {
        logger.error(error, 'Error processing cart items for RCare');
      }
    };

    processItems();
  }, [lineItems]);

  const cartItemsCount = generateCartItemsCount(
    rearrangedLineItems,
    includesTradeIn,
  );

  const itemPskuMap: ItemPskuMap = useMemo(
    () =>
      cartItemsWithoutRCare.reduce((prev, item) => {
        const {
          psku,
        }: {
          psku?: string;
        } = extractTags(item.merchandise.product.tags);

        if (!psku) return prev;

        return {
          ...prev,
          [psku]: item,
        };
      }, {}),
    [
      cartItemsWithoutRCare.reduce(
        (prev, l) => `${prev}#${l.id}-${l.quantity}`,
        '',
      ),
    ],
  );

  const [warrantyUpsellShown, setWarrantyUpsellShown] = useState(false);
  const [warrantyUpsellList, setWarrantyUpsellList] = useState<
    warrantyUpsellT[]
  >([]);

  useEffect(() => {
    if (lineItems && !showWarrantyUpsell) {
      const processWarrantyUpsellList = async () => {
        const updatedWarrantyUpsellList = await fetchUpdatedWarrantyUpsellList(
          lineItems,
          warrantyEligibilityMap,
        );

        setWarrantyUpsellList(updatedWarrantyUpsellList as warrantyUpsellT[]);
      };

      processWarrantyUpsellList();
    }
  }, [lineItems, showWarrantyUpsell, warrantyEligibilityMap]);

  useEffect(() => {
    if (router.locale) setLang(router.locale);
  }, [router.locale]);

  if (isLoadingCart) return <LoadingCart />;
  if (isInvalidCart) return <EmptyCart />;

  const totalCompareprice = Number(
    totalCompareAt(cart, comparePrices).toFixed(2),
  );

  const totalCart = Number(cart.estimatedCost.subtotalAmount.amount) || 0;

  let sameVendor = true;

  const vendorCartItems = rearrangedLineItems.filter(
    (item) =>
      !(
        Object.prototype.hasOwnProperty.call(item, 'rCareSpecs') ||
        item.merchandise.product.productType === 'Gift Cards'
      ),
  );
  const vendorBase = vendorCartItems[0]?.merchandise?.product?.vendor;

  sameVendor = vendorCartItems.every(
    (x) => x?.merchandise?.product?.vendor === vendorBase,
  );

  /*
   * RT-4875
   * Proceed to remove OOS cart line item(s)
   */
  const clickToProceedCartLineItemRemoval = async () => {
    if (lineItemsToBeRemoved.length > 0) {
      await deleteCartLines(lineItemsToBeRemoved, cart.id);
      setLineItemsToBeRemoved([]);
      window.location.reload();
    }
  };

  const checkoutButtonClicked = async () => {
    try {
      logger.info({ cart }, 'starting checkout');

      /*
       * RT-4875
       * Check the quantity of the merchandise excluding express shipping and reebelocare
       */
      const toBeRemovedCartLineItems: string[] =
        await findOutOfStockCartLineItems(lineItems);

      // Display message to the user
      if (toBeRemovedCartLineItems.length > 0) {
        setLineItemsToBeRemoved(toBeRemovedCartLineItems);
        setModalVisible(true);

        return;
      }

      GTM.event('checkout', {
        checkout: {
          actionField: {
            step: 1,
          },
          products: lineItems.map((lineItem) => ({
            quantity: lineItem.quantity,
            id: lineItem.merchandise.id,
            name: lineItem.merchandise.product.title,
            price: lineItem.merchandise.priceV2.amount,
          })),
        },
      });

      const sessionId = String((await analytics.user()).anonymousId());
      const { userAgent } = navigator;

      const query: Record<string, string> = {
        cartId: cart.id,
        sessionId,
        userAgent,
      };

      if (settings.store === 'reebelo-au' && zipcodeStateData) {
        query.zipcode = zipcodeStateData?.zipcode;
        query.state = zipcodeStateData?.state;
      }
      if (lang && lang === 'fr') query.lang = 'fr';

      const { checkoutUrl } = await fetch(
        `/api/checkout?${new URLSearchParams(query)}`,
      ).then((res) => res.json());

      trackCheckoutStarted({
        cart,
        properties: {
          email: customer?.email,
          new_checkout: true,
        },
      });

      if (checkoutUrl) {
        logger.info({ cart, checkoutUrl }, 'redirecting checkout');
        router.push(checkoutUrl);
      } else {
        logger.warn({ cart, checkoutUrl }, 'error redirecting checkout');
      }
    } catch (error: any) {
      logger.error({ cart, error }, 'Error starting checkout');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleSummaryClick = () => toggleItems(!isItemsVisible);

  const rearrangedPskus = rearrangedLineItems.map(
    (lineItem) => extractTags(lineItem.merchandise.product.tags)?.psku ?? '',
  );

  const includesExpressShipping = rearrangedLineItems.some((item) =>
    item.merchandise.product.title.toLowerCase().includes('express shipping'),
  );

  return (
    <CartContext.Provider value={CartHelpers}>
      <Head>
        <title>{t`Your Shopping Cart`}</title>
        <WebsiteSchema />
      </Head>
      <BackToTopButton />
      <CartItemRemovedModal
        isVisible={isModalVisible}
        hideModal={async () => {
          setModalVisible(false);
          await clickToProceedCartLineItemRemoval();
        }}
      />
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <div className="relative bg-gray-300">
        <div className="absolute left-0 hidden h-full w-[calc(50%+14rem)] bg-white xl:block" />
        <div className="xl:reebelo-container relative z-20 pb-10">
          <div className="reebelo-container xl:px-0">
            <div className="flex xl:hidden">
              <BreadCrumb />
            </div>
          </div>
          <div className="flex flex-wrap gap-0 sm:gap-10 xl:flex-row-reverse xl:flex-nowrap">
            <div className="z-[2] w-full  xl:w-[35%]">
              {totalCompareprice > totalCart && (
                <h2 className="mb-2 mt-4 hidden w-full whitespace-nowrap text-center text-base font-bold text-gray-900 xl:block">
                  {t`Good choice! You saved ${{
                    price: priceFormater.long(totalCompareprice - totalCart),
                  }}!`}
                </h2>
              )}
              <div className="relative bg-white xl:bg-transparent">
                <div className="reebelo-container relative xl:px-0">
                  <div className="flex w-full flex-wrap rounded-[5px] bg-white shadow-none xl:shadow-md">
                    <CartInfo
                      setIsCheckingOut={setIsCheckingOut}
                      isCheckingOut={isCheckingOut}
                      setShowWarrantyUpsell={setShowWarrantyUpsell}
                      warrantyUpsellList={warrantyUpsellList}
                      isLoading={loading}
                      totalCart={totalCart}
                      isItemsVisible={isItemsVisible}
                      cartItemsCount={cartItemsCount}
                      onSummaryClick={handleSummaryClick}
                      totalCompareprice={totalCompareprice}
                      onCheckoutClick={checkoutButtonClicked}
                      warrantyUpsellShown={warrantyUpsellShown}
                      setWarrantyUpsellShown={setWarrantyUpsellShown}
                      includesExpressShipping={includesExpressShipping}
                    />
                    {rearrangedLineItems.map((lineItem, index) => (
                      <CartItem
                        isUpgradeGuideOpen={isUpgradeGuideOpen}
                        handleUpgradedOpen={handleUpgradedOpen}
                        cart={cart}
                        index={index}
                        lineItem={lineItem}
                        lineItemId={lineItem.id}
                        key={`${lineItem.id}-${index}`}
                        isItemsVisible={isItemsVisible}
                        setComparePrices={(price) =>
                          setComparePrices((obj) => ({
                            ...obj,
                            [lineItem.id]: price,
                          }))
                        }
                      />
                    ))}
                    <TradeInItem isItemsVisible={isItemsVisible} />
                    {!sameVendor && (
                      <div className="group flex w-full border-b p-4 pl-0 pr-0 last:mb-0 xl:p-5 xl:pl-5 xl:pr-5">
                        <Image
                          src={infoIconBlack}
                          alt="Vendor Nudge"
                          width={12}
                          height={12}
                          className="object-contain"
                        />
                        <div className="ml-2 text-xs font-light">
                          These items will be shipped in separate packages
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-0 py-5 shadow-md lg:p-5 lg:px-5 xl:p-0 xl:shadow-none">
                <div className="reebelo-container w-full  xl:px-0">
                  <div className="mb-0 flex w-full flex-wrap xl:hidden">
                    <div className="flex w-full flex-wrap px-1">
                      <PaymentIcons largerIcon={true} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 hidden overflow-hidden rounded-[5px] bg-white shadow-md xl:block">
                <CartStaticInfo lineItems={lineItems} />
              </div>
            </div>
            <div className="reebelo-container w-full items-stretch xl:flex xl:w-[65%] xl:px-0">
              <div
                className="relative mt-8 flex w-full flex-col rounded-[5px] bg-gray-300 p-0 sm:mt-0 xl:w-full xl:rounded-none xl:bg-white xl:pr-10 xl:pl-0 xl:pt-0"
                id="e2e-cart-line-items-container"
              >
                <div className="hidden xl:flex">
                  <BreadCrumb />
                </div>
                <RecommendedItems
                  cart={cart}
                  itemPskuMap={itemPskuMap}
                  recommendations={recommendations}
                  isLoading={recommendationsLoading}
                  rearrangedPskus={rearrangedPskus}
                />
                {/* Best Sellers  */}
                <BestSellers className="pb-8" categories={categories} />
                {/* Popular Categories */}
                <PopularCategories isCartPage />
                <div className="mt-auto hidden xl:block">
                  <PaymentIcons largeBox={true} />
                </div>
              </div>
              <div className="mt-2 flex w-full flex-wrap rounded-[5px] bg-white shadow-md xs:mt-4 xl:hidden">
                <div className="flex w-full flex-wrap">
                  <CartStaticInfo lineItems={lineItems} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isEmpty(warrantyEligibilityMap) && warrantyUpsellList && (
        <WarrantyUpsell
          showModal={showWarrantyUpsell}
          setShowModal={setShowWarrantyUpsell}
          warrantyUpsellList={warrantyUpsellList}
          setIsCheckingOut={setIsCheckingOut}
          isCheckingOut={isCheckingOut}
          onCheckoutClick={checkoutButtonClicked}
        />
      )}
    </CartContext.Provider>
  );
}
